import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
} from "../sendCopy";
import { BODY_TEXT } from "../ui";
import { useSendFlow } from "../useSendFlow";

/**
 * "Your Kund": each Hawan booking's Kund number, the route on the map,
 * arrival details and its entry pass. Only bookings with a Kund get it;
 * allocate on the event page first.
 */
export function YourKundPanel({
  eventSlug,
  audienceCount,
  withoutKund,
  defaultTestEmail,
  senderName,
  alreadySentNote,
}: {
  eventSlug: string;
  /** Coming bookings with a Kund and an email address. */
  audienceCount: number;
  /** Coming bookings still without a Kund. */
  withoutKund: number;
  defaultTestEmail: string;
  senderName: string | null;
  alreadySentNote: string | null;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const utils = trpc.useUtils();
  const send = trpc.adminSendHawanAllocation.useMutation();

  const run = (mode: "test" | "all") => {
    send.mutate(
      {
        eventSlug,
        mode,
        testEmail: mode === "test" ? flow.testAddress.trim() : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped
                ? EMAIL_OFF_SENTENCE
                : `Test sent to ${res.sentTo} with a sample Kund. Read it through, then send to everyone with a Kund.`,
            );
          } else if (res.mode === "all") {
            flow.sendAllDone(
              sendAllSentence(
                res,
                "No booking has a Kund yet, so nothing was sent.",
              ),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  return (
    <>
      <p className={BODY_TEXT}>
        Each booking gets its Kund number, the way to it on the map, when to
        arrive, parking, and its entry pass. Allocate Kunds on the event page
        first: bookings without one are skipped.
        {withoutKund > 0
          ? ` Right now ${people(withoutKund)} ${withoutKund === 1 ? "has" : "have"} no Kund yet.`
          : ""}
      </p>
      <TestThenSend
        flow={flow}
        emailName="Your Kund"
        audience={audienceCount > 0 ? people(audienceCount) : null}
        noAudienceText="No booking has a Kund yet. Allocate them on the event page, then come back here."
        busy={send.isPending}
        onSendTest={() => run("test")}
        onSendAll={() => run("all")}
        alreadySentNote={alreadySentNote}
      />
    </>
  );
}
